import { z } from 'zod'

// Vue en direct du navigateur du collecteur pendant un défi (décision du 02/10/2026) : le
// titulaire franchit lui-même un CAPTCHA, une connexion à faire soi-même ou des conditions à
// accepter, en voyant l'écran du Chromium du collecteur et en le touchant depuis son téléphone.
// Ce n'est pas un contournement : c'est un humain qui le fait. Règles pures : les gestes permis
// (cliquer, faire défiler, écrire, quelques touches), rien d'autre (ni adresse, ni précédent, ni
// onglet, ni raccourci, ni fichier, ni presse-papiers), et les messages du relais.

/** Touches de base : Entrée, Effacer, Tab, flèches. */
export const TOUCHES_VUE = [
  'Enter',
  'Backspace',
  'Tab',
  'ArrowLeft',
  'ArrowRight',
  'ArrowUp',
  'ArrowDown',
] as const
export type ToucheVue = (typeof TOUCHES_VUE)[number]

/** Position sur l'image affichée, en fraction de sa largeur et de sa hauteur. */
const fraction = z.number().min(0).max(1)
const defilement = z.number().int().min(-2000).max(2000)

export const gesteVue = z.discriminatedUnion('type', [
  z.object({ type: z.literal('clic'), x: fraction, y: fraction }).strict(),
  z
    .object({
      type: z.literal('molette'),
      x: fraction,
      y: fraction,
      dx: defilement,
      dy: defilement,
    })
    .strict(),
  z
    .object({
      type: z.literal('texte'),
      // Ni caractère de contrôle ni saut de ligne : Entrée est une touche à part.
      texte: z
        .string()
        .min(1)
        .max(200)
        // eslint-disable-next-line no-control-regex
        .regex(/^[^\u0000-\u001f\u007f]+$/),
    })
    .strict(),
  z.object({ type: z.literal('touche'), touche: z.enum(TOUCHES_VUE) }).strict(),
])
export type GesteVue = z.infer<typeof gesteVue>

/** Pixels du viewport du navigateur pour une position sur l'image (bords compris). */
export function pointViewport(
  p: { x: number; y: number },
  viewport: { largeur: number; hauteur: number },
): { x: number; y: number } {
  const borne = (v: number, max: number) => Math.min(max - 1, Math.max(0, Math.round(v * max)))
  return { x: borne(p.x, viewport.largeur), y: borne(p.y, viewport.hauteur) }
}

/** Ce que le navigateur de l'humain envoie au relais. */
export const messageSpectateurVue = z.discriminatedUnion('type', [
  z.object({ type: z.literal('geste'), geste: gesteVue }).strict(),
  z.object({ type: z.literal('terminer') }).strict(),
])
export type MessageSpectateurVue = z.infer<typeof messageSpectateurVue>

/**
 * Issue d'une vue : connecté (le robot reprend), autre étape (un code suit), expirée, abandonnée
 * (« Pas maintenant »), bloquée (navigation hors des domaines), terminée par l'humain (le défi
 * reste ouvert jusqu'à l'expiration), coupure (relais ou collecteur perdu).
 */
export const ISSUES_VUE = [
  'connecte',
  'autre_etape',
  'expiree',
  'abandonnee',
  'bloquee',
  'terminee',
  'coupure',
] as const
export type IssueVue = (typeof ISSUES_VUE)[number]

/** Ce que le collecteur envoie au relais, en plus des images (binaires, JPEG). */
export const messageCollecteurVue = z.discriminatedUnion('type', [
  z
    .object({
      type: z.literal('pret'),
      largeur: z.number().int().positive(),
      hauteur: z.number().int().positive(),
    })
    .strict(),
  z.object({ type: z.literal('etat'), requetesCoupees: z.number().int().min(0) }).strict(),
  z.object({ type: z.literal('fin'), issue: z.enum(ISSUES_VUE) }).strict(),
])
export type MessageCollecteurVue = z.infer<typeof messageCollecteurVue>

/** Ce que le relais envoie au collecteur. */
export type MessageRelaisVue =
  | { type: 'spectateur'; present: boolean }
  | { type: 'geste'; geste: GesteVue }
  | { type: 'terminer' }

/** Images : JPEG, assez nettes pour lire un CAPTCHA sur un téléphone, cinq par seconde au plus. */
export const IMAGE_VUE = { qualite: 60, largeurMax: 1280, hauteurMax: 1280 } as const
export const CADENCE_MIN_MS = 200
/** Au-delà, une image attend : un téléphone lent ne fait pas gonfler la mémoire. */
export const TAMPON_MAX_OCTETS = 2 * 1024 * 1024
/** Jeton d'ouverture de la vue : à usage unique, lié au défi et à la personne. */
export const SECONDES_JETON_VUE = 60
